import React, {
  useState,
  useCallback,
  useMemo,
  InputHTMLAttributes,
} from "react";
import debounce from "lodash/debounce";
import classNames from "classnames";
import Icon from "components/Icon/Icon";
import type { IconNames } from "components/Icon/Icon";

export const defaultDebounceInputClassName =
  "w-full p-2 text-slate-700 border border-slate-200 bg-white rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 hover:border-primary";

export type Props = InputHTMLAttributes<HTMLInputElement> & {
  value?: string;
  onChange?: (val: string) => void;
  debounceTimeout?: number;
  error?: boolean;
  onPressEnterKey?: () => void;
  icon?: IconNames;
};

function DebounceInput({
  value,
  onChange,
  debounceTimeout = 300,
  type = "text",
  className,
  error,
  onPressEnterKey,
  icon,
  ...rest
}: Props) {
  const [localValue, setLocalValue] = useState<string | undefined>(value);

  const setDebouncedChange = useMemo(
    () =>
      onChange
        ? debounce((val: string) => {
            onChange(val);
            setLocalValue(undefined);
          }, debounceTimeout)
        : undefined,
    [onChange, debounceTimeout]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setLocalValue(e.target.value);
      setDebouncedChange?.(e.target.value);
    },
    [setDebouncedChange]
  );

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      onPressEnterKey?.();
    }
  };

  const input = (
    <input
      className={classNames(
        error ? "error-input" : "text-input",
        icon && "!pl-8",
        className
      )}
      type={type}
      value={localValue ?? value ?? ""}
      onChange={handleInputChange}
      onKeyDown={handleKeyDown}
      autoComplete="new-password"
      {...rest}
    />
  );

  if (!icon) return input;

  return (
    <div className="relative">
      <Icon
        name={icon}
        className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-primary-500 pointer-events-none"
      />
      {input}
    </div>
  );
}

export default DebounceInput;
